/**
 * Estimates the number of tokens in a given text string.
 * This is a rough approximation based on common tokenization patterns.
 * 
 * @param text - The text to estimate tokens for
 * @returns The estimated number of tokens
 */
export function estimateTokens(text: string): number {
    if (!text || text.trim().length === 0) {
        return 0;
    }

    // Remove extra whitespace and normalize
    const normalizedText = text.trim().replace(/\s+/g, ' ');
    
    // Rough estimation: 
    // - Average English word is ~1.3 tokens
    // - Punctuation and special characters add tokens
    // - This is a simplified approximation
    const words = normalizedText.split(' ').length;
    const characters = normalizedText.length;
    
    // More sophisticated estimation considering:
    // - Words typically average 1.3 tokens
    // - Punctuation and special characters
    // - Common subwords and prefixes/suffixes
    const estimatedTokens = Math.ceil(words * 1.3 + (characters - words) * 0.1);
    
    return Math.max(1, estimatedTokens); // At least 1 token for non-empty text
}

/**
 * Formats the token count with German text
 * 
 * @param tokenCount - The number of tokens
 * @returns Formatted string like "N Token (gesch.)"
 */
export function formatTokenEstimation(tokenCount: number): string {
    return `${tokenCount} Token (gesch.)`;
}

/**
 * Simple token estimation using character count divided by 4
 * This matches the PR requirement for basic token estimation
 * 
 * @param text - The text to estimate tokens for
 * @returns The estimated number of tokens
 */
export function estimateTokensSimple(text: string): number {
    if (!text || text.trim().length === 0) {
        return 0;
    }
    return Math.max(1, Math.ceil(text.length / 4));
}

/**
 * Hook for real-time token estimation
 * 
 * @param text - The text to estimate tokens for
 * @returns The formatted token estimation string
 */
export function useTokenEstimation(text: string): string {
    const tokenCount = estimateTokens(text);
    return formatTokenEstimation(tokenCount);
}
